// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useNavigate } from '@tanstack/react-router';

// material ui icons
import BoltIcon from '@mui/icons-material/Bolt';
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded';
import WaterDropRoundedIcon from '@mui/icons-material/WaterDropRounded';

// material ui
import { Box, CircularProgress, Grid, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

// api
import type { PortfolioAnalysisOutput } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysis';
import type { GetAlarmDto } from '@/api/generated/recalc/endpoints/v2/alarms';

// feature-components
import EnergyXplorerAlarmManagementActions from '@/features/energyxplorer/components/alarms/EnergyXplorerAlarmManagementActions';
import EnergyXplorerAlarmSetupCard from '@/features/energyxplorer/components/portfolio-dashboard/EnergyXplorerAlarmSetupCard';
import EnergyXplorerAlarmsGrid from '@/features/energyxplorer/components/portfolio-dashboard/EnergyXplorerAlarmsGrid';
import PortfolioKpiCard from '@/features/energyxplorer/components/portfolio-dashboard/PortfolioKpiCard';
import PortfolioOverviewDatagrid from '@/features/energyxplorer/components/portfolio-dashboard/PortfolioOverviewDatagrid';
//import PortfolioMonthlyChart from '@/features/energyxplorer/components/portfolio-dashboard/PortfolioMonthlyChart';
import PortfolioCard from '@/features/energyxplorer/components/sales-cards/PortfolioCard';

// feature-hooks
import { useEnergyXplorerAlarmSetup } from '@/features/energyxplorer/hooks/useEnergyXplorerAlarmSetup';

// components
import { useHasService } from '@/hooks/useHasService';

type PortfolioSubscription =
  { power?: boolean; water?: boolean; heat?: boolean } | undefined;

interface PortfolioModuleProps {
  accountId: string | undefined;
  portfolioTotals: {
    power: { current?: number; last?: number };
    heat: { current?: number; last?: number };
    water: { current?: number; last?: number };
  };
  typedSubscription: PortfolioSubscription;
  portfolioAlarms: GetAlarmDto[];
  isPortfolioAlarmsLoading: boolean;
  powerData: PortfolioAnalysisOutput | undefined;
  heatData: PortfolioAnalysisOutput | undefined;
  waterData: PortfolioAnalysisOutput | undefined;
  hasAnalyticsService: boolean;
}

const PortfolioModule = ({
  accountId,
  portfolioTotals,
  typedSubscription,
  portfolioAlarms,
  isPortfolioAlarmsLoading,
  powerData,
  heatData,
  waterData,
  hasAnalyticsService,
}: PortfolioModuleProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation('energy-analysis');
  const powerPath = `/${accountId}/energyxplorer/portfolio/power/analysis/overview`;
  const heatPath = `/${accountId}/energyxplorer/portfolio/heat/analysis/overview`;
  const waterPath = `/${accountId}/energyxplorer/portfolio/water/analysis/overview`;

  const { hasService, isLoading } = useHasService('PortfolioAnalysis');
  const {
    shouldShowSetupPrompt,
    isLoading: isAlarmSetupLoading,
    isEnabling,
    enableAlarms,
    autoCreatedAlarmGroup,
    canDeleteAutoCreatedAlarmGroup,
    isDeletingAutoCreatedAlarmGroup,
    deleteAutoCreatedAlarmGroup,
  } = useEnergyXplorerAlarmSetup({
    accountId: accountId ? Number(accountId) : undefined,
    enabled: hasAnalyticsService,
  });
  const shouldRenderSetupPrompt = !isAlarmSetupLoading && shouldShowSetupPrompt;

  if (isLoading) return <CircularProgress />;

  if (!hasService && !isLoading) return <PortfolioCard />;

  return (
    <Box>
      {/* Module Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 1,
        }}
      >
        <Box flex={1}>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
            {t('portfolio.module.title')}
          </Typography>
        </Box>
      </Box>

      {/* KPI Cards */}
      <Stack spacing={2}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <PortfolioKpiCard
              title={t('portfolio.cards.power.title')}
              icon={<BoltIcon fontSize="large" />}
              color={theme.palette.power?.main ?? theme.palette.primary.main}
              path={powerPath}
              onClick={() =>
                navigate({
                  to: powerPath,
                })
              }
              totalCurrentYear={portfolioTotals.power.current}
              totalLastYear={portfolioTotals.power.last}
              unit="kWh"
              enabled={typedSubscription?.power}
              summedData={powerData?.summed_data}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <PortfolioKpiCard
              title={t('portfolio.cards.heat.title')}
              icon={<LocalFireDepartmentRoundedIcon fontSize="large" />}
              color={theme.palette.heat?.main ?? theme.palette.error.main}
              path={heatPath}
              onClick={() =>
                navigate({
                  to: heatPath,
                })
              }
              totalCurrentYear={portfolioTotals.heat.current}
              totalLastYear={portfolioTotals.heat.last}
              unit="kWh"
              enabled={typedSubscription?.heat}
              summedData={heatData?.summed_data}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <PortfolioKpiCard
              title={t('portfolio.cards.water.title')}
              icon={<WaterDropRoundedIcon fontSize="large" />}
              color={theme.palette.water?.main ?? theme.palette.info.main}
              path={waterPath}
              onClick={() =>
                navigate({
                  to: waterPath,
                })
              }
              totalCurrentYear={portfolioTotals.water.current}
              totalLastYear={portfolioTotals.water.last}
              unit="m³"
              enabled={typedSubscription?.water}
              summedData={waterData?.summed_data}
            />
          </Grid>
        </Grid>
        {/* <PortfolioMonthlyChart
          powerData={powerData}
          heatData={heatData}
          waterData={waterData}
          subscription={typedSubscription}
        /> */}
        <Grid container spacing={2}>
          <Grid size={12}>
            <PortfolioOverviewDatagrid
              powerData={powerData}
              heatData={heatData}
              waterData={waterData}
            />
          </Grid>
        </Grid>

        {hasAnalyticsService ? (
          shouldRenderSetupPrompt ? (
            <EnergyXplorerAlarmSetupCard
              accountId={accountId}
              title={t('portfolio.alarms_list.title')}
              onEnable={enableAlarms}
              isLoading={isEnabling}
              autoCreatedAlarmGroupName={autoCreatedAlarmGroup?.Name}
              canDeleteAutoCreatedAlarmGroup={canDeleteAutoCreatedAlarmGroup}
              isDeletingAutoCreatedAlarmGroup={isDeletingAutoCreatedAlarmGroup}
              onDeleteAutoCreatedAlarmGroup={deleteAutoCreatedAlarmGroup}
            />
          ) : (
            <EnergyXplorerAlarmsGrid
              accountId={accountId}
              alarms={portfolioAlarms}
              isLoading={isPortfolioAlarmsLoading || isAlarmSetupLoading}
              emptyText={t('portfolio.alarms_list.empty')}
              title={t('portfolio.alarms_list.title')}
              toolbarActions={
                <EnergyXplorerAlarmManagementActions
                  accountId={accountId}
                  autoCreatedAlarmGroupName={autoCreatedAlarmGroup?.Name}
                  canDeleteAutoCreatedAlarmGroup={
                    canDeleteAutoCreatedAlarmGroup
                  }
                  isDeletingAutoCreatedAlarmGroup={
                    isDeletingAutoCreatedAlarmGroup
                  }
                  onDeleteAutoCreatedAlarmGroup={deleteAutoCreatedAlarmGroup}
                />
              }
            />
          )
        ) : null}
      </Stack>
    </Box>
  );
};

export default PortfolioModule;
